import React, { useEffect, useState } from "react";
import cx from "classnames";
import { LoadingDots } from "@qgiv/core-react";
import NoResultsFound from "./NoResultsFound";
import SearchResult from "./SearchResult";
import SearchResultsCancelButton from "./SearchResultsCancelButton";
import "./SearchResults.scss";

/**
 * @param {object} props
 * @param {string} props.spinnerColor
 * @param {boolean} props.isDtD
 * @param {boolean} props.searching
 * @param {Array} props.searchResults
 * @param {Function} props.handleResultClick
 * @param {Function} props.handleResultKeyDown
 * @param {Function} props.handleCancelButton
 * @returns {React.JSX.Element} The rendered search results component.
 */
const SearchResults = ({
    spinnerColor,
    isDtD,
    searching,
    searchResults,
    handleResultClick,
    handleResultKeyDown,
    handleCancelButton,
}) => {
    const [spinnerPainted, setSpinnerPainted] = useState(false);

    const combinedKeyHandlers = (e, index) => {
        // handles the cancel button keydown event
        handleCancelButton(e);
        // handles accessibility
        handleResultKeyDown(e, index, searchResults);
    };
    const handleFocus = (index) => {
        const results = document.getElementById("gift-matching__result-0");

        if (results && index === 0) {
            results.setAttribute("aria-selected", "true");
        }
    };

    // the purpose of this useEffect is to trigger the mutation observer resize logic
    // this must be done after the search results loading spinner has expanded the container
    // via a css transition. The mutation observer will see this class attribute change and then
    // trigger the resize logic now considering the expanded search container in its height calculation.
    // 20ms was the minimum delay that worked so for safety we are using 40ms. If issues arise related
    // to delayed resizing of the modal for matching gifts, this is the value that should be adjusted.
    const mutationObserverDelay = 40;
    useEffect(() => {
        if (searching) {
            setTimeout(() => {
                setSpinnerPainted(true);
            }, mutationObserverDelay);
        } else {
            setSpinnerPainted(false);
        }

        return () => {
            setSpinnerPainted(false);
        };
    }, [searching]);

    const hasSearchResults = !searching && searchResults.length > 0;
    const hasMatchResults =
        searchResults?.[0]?.id !== "no match" && searchResults.length > 0;

    const displayResults = () => (
        <div
            className="gift-matching__results"
            id="gift-matching__results"
            role="listbox"
        >
            {searchResults.map((result, index) => {
                const _key = `-${index}`;
                return result.id === "no match" ? (
                    <NoResultsFound
                        key={_key}
                        handleCancelButton={handleCancelButton}
                        handleOnKeyDown={handleCancelButton}
                    />
                ) : (
                    <SearchResult
                        key={_key}
                        result={result}
                        handleFocus={handleFocus}
                        handleResultClick={handleResultClick}
                        handleResultKeyDown={handleResultKeyDown}
                        isDtD={isDtD}
                        index={index}
                        searchResults={searchResults}
                    />
                );
            })}
            {hasMatchResults && (
                <SearchResultsCancelButton
                    combinedKeyHandlers={combinedKeyHandlers}
                    handleCancelButton={handleCancelButton}
                />
            )}
        </div>
    );
    return (
        <div
            className={cx(
                "gift-matching__search-container",
                searching && "gift-matching__search-container--loading",
                spinnerPainted && "js-resize-offset-height",
                hasSearchResults && "gift-matching__search-container--expanded",
            )}
            id="gift-matching-search-container"
        >
            {searching ? (
                <LoadingDots color={spinnerColor} center />
            ) : (
                displayResults()
            )}
        </div>
    );
};

export default SearchResults;
